import {
    Component,
    ComponentInterface,
    Element,
    forceUpdate,
    h,
    Host,
    Listen,
    Method,
    Prop,
    State,
} from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import {
    FilterData,
    FilterDataProvider,
    FilterDataProviderState,
    getFilterDataProvider,
} from "../../apis/stateful-content";
import { parseFilterData, renderFilterBlock } from "./stateful-filter-utils";
import {
    getTranslateFn,
    TranslateFn,
    Translations,
    getTranslationSource,
    TranslationRenderType,
} from "../stateful-content/stateful-content-utils";

@Component({
    tag: "ux-iws-stateful-filter",
    styleUrl: "stateful-filter.scss",
})
export class StatefulFilter implements ComponentInterface {
    private baseClass = "ux-iws-stateful-filter";
    private filterProvider: FilterDataProvider;
    private translate: TranslateFn;
    private translationRenderType: TranslationRenderType =
        TranslationRenderType.Filter;
    private hasSidebar: boolean = false;
    private temporaryFilterQuery: string = "";
    private filterChangeCounter: number = 0; // workaround to enforce re-rendering of filter tags

    @Element() domNode: HTMLUxIwsStatefulFilterElement;

    /** Toggles the visibility of the date picker */
    @Prop() datePicker: boolean = false;
    /** Range start filter name */
    @Prop() rangeStartName: string = "start_date";
    /** Range end filter name */
    @Prop() rangeEndName: string = "end_date";
    /** Initial range start date (YYYY-MM-DD) */
    @Prop() rangeStartValue: string = "";
    /** Label text for the range end field */
    @Prop() rangeStartLabel?: string;
    /** Label text for the range end field */
    @Prop() rangeEndLabel?: string;
    /** Initial range end date (YYYY-MM-DD) */
    @Prop() rangeEndValue: string = "";
    /** Minimum date value */
    @Prop() minDate?: string;
    /** Maximum date value */
    @Prop() maxDate?: string;
    /** Format of the date value */
    @Prop() dateFormat: string = "YYYY-MM-DD";
    /** Toggles the visibility of active filter tags */
    @Prop() disableTags: boolean = false;
    /** custom class for all filter blocks */
    @Prop() filterColumnClass: string = "";

    /** print debug info */
    @Prop() debug: boolean = false;
    /** scope used to retrieve the filter data provider */
    @Prop() scope: string = "";
    /** @deprecated JSON string or object with translations. Use Slot "translations" instead */
    @Prop() translations: Translations | string = "";
    /** endpoint where filter data can be requested */
    @Prop() url: string = "";
    /** optional language for translations (mainly used for storybook) - otherwise language is automatically detected by ux.i18n */
    @Prop() language?: string = "";
    /** defines whether the component should use instant filtering */
    @Prop() instantFiltering: boolean = false;

    @State() filterData: FilterData | null = null;

    private _log = (...args: any[]) => {
        if (this.debug && console && typeof console.log === "function") {
            console.log(`[Stateful-Filter]:`, ...args);
        }
    };

    @Listen("uxToggle", { target: "window" })
    favToggleUpdate() {
        if (this.filterProvider === undefined) {
            return;
        }
        this.filterProvider.getData();
    }

    componentWillLoad() {
        this.filterProvider = getFilterDataProvider(this.scope);
        this.filterProvider.subscribe(this.handleFilterProviderState);

        this.handleFilterProviderState(this.filterProvider.state);
        this.filterProvider.setEndpoint(this.url);

        this.setTranslations();
        const sidebar = this.domNode.closest("ux-sidebar");
        if (sidebar !== null) {
            this.hasSidebar = true;
            sidebar.addEventListener("uxSidebarVisibilityChanged", (e: any) => {
                if (this.instantFiltering) return;
                if (!e.detail.opened) {
                    this.filterProvider.setFilterQuery(
                        this.temporaryFilterQuery
                    );
                } else {
                    this.temporaryFilterQuery = this.filterProvider.filterQuery;
                }
            });
        }
        this.preSelectFiltersFromHash(window.location.hash);
    }

    /**
     * this enables us to set a filter from a link via a hash in the URL:
     * https://trumpf.com/example#stateful-filter:mymissions?key1=value1&key2=value2
     * "mymissions" must be the same scope as this.scope
     * "key1" and "key2" are filter names + their values
     **/
    private preSelectFiltersFromHash = (hash: string) => {
        const filterType = hash.substring(1, hash.indexOf(":"));
        const scope = hash.substring(hash.indexOf(":") + 1, hash.indexOf("?"));
        if (filterType === "stateful-filter" && scope === this.scope) {
            // split "key1=value1&key2=value2" into [["key1", "value1"], ["key2", "value2"]]
            const filterPairs = hash
                .substring(hash.indexOf("?") + 1)
                .split("&")
                .map((pair) => pair.split("="))
                .filter((item) => item.length === 2); // we only use items that have also a value
            if (filterPairs.length > 0) {
                // create a filterQuery like "key1=value1&key2=value2" but with URI-encoded strings
                const filterQuery = filterPairs
                    .map((pair) =>
                        pair
                            .map((item) =>
                                encodeURIComponent(decodeURIComponent(item))
                            )
                            .join("=")
                    )
                    .join("&");
                this.filterProvider.setFilterQuery(filterQuery);
            }
        }
    };

    /**
     * handle script tag translations as well as deprecated translations prop fallback
     **/
    private setTranslations = () => {
        const translationsObject = this.translations.length
            ? this.translations
            : getTranslationSource(
                  this.scope,
                  this.domNode,
                  this.translationRenderType
              );
        this.translate = getTranslateFn(translationsObject);
    };

    private handleFilterProviderState = ({
        filterData,
        filterQuery,
    }: FilterDataProviderState) => {
        if (filterData === null) {
            return;
        }
        this._log(`handleFilterProviderState filterQuery: "${filterQuery}"`);
        this.filterChangeCounter++;
        this.filterData = filterData;
        forceUpdate(this);
    };

    private handleFilterChange = (event: Event) => {
        const element = event.target as HTMLElement;
        const form: HTMLFormElement = element.closest(
            "form"
        ) as HTMLFormElement;
        if (form === null) {
            return;
        }
        const filterQuery = parseFilterData(form);
        const filterNames = [...form.querySelectorAll("input, select")].map(
            (el: HTMLInputElement | HTMLSelectElement) =>
                encodeURIComponent(el.name)
        );
        // handle elements that are not located inside the form
        const externalFilterQuery = this.filterProvider.filterQuery
            .split("&")
            .filter((query) => {
                return (
                    !filterNames.includes(query.split("=")[0]) && query !== ""
                );
            });
        this._log(`handleFilterChange event (query: "${filterQuery}")`);
        this.changeFilterQuery(
            [
                ...externalFilterQuery,
                ...filterQuery.split("&").filter((query) => query !== ""),
            ].join("&")
        );
    };

    /** change the filter query.
     * If the filter is inside a ux-sidebar and instantFiltering is not active, the filter will be applied as soon as the sidebar has been closed  */
    @Method()
    async changeFilterQuery(filterQuery: string) {
        if (this.hasSidebar && !this.instantFiltering) {
            this.temporaryFilterQuery = filterQuery;
        } else {
            this.filterProvider.setFilterQuery(filterQuery);
        }
    }

    private handleRemoveAll = () => {
        this.changeFilterQuery("");
    };

    private handleDateRangeChange = (event: CustomEvent) => {
        setTimeout(() => {
            this.handleFilterChange(event);
        }, 10); // this timeout is necessary to ensure that the date range picker has updated its values
    };

    private getDatePickerValues = (): {
        startDate: string;
        endDate: string;
    } => {
        const params = new URLSearchParams(this.filterProvider.filterQuery);
        return {
            startDate: params.get(this.rangeStartName) || this.rangeStartValue,
            endDate: params.get(this.rangeEndName) || this.rangeEndValue,
        };
    };

    render() {
        const { t } = i18nProvider;
        const { filterData } = this;
        const { startDate, endDate } = this.getDatePickerValues();
        const { pending } = this.filterProvider;

        return (
            <Host class={this.baseClass}>
                {filterData && filterData.filterBlocks.length > 0 && (
                    <ux-filter
                        onChange={this.handleFilterChange}
                        key={`${this.filterChangeCounter}`}
                    >
                        {this.datePicker && (
                            <div class="ux-row">
                                <div class="ux-col-12 ux-u-mb-s">
                                    <ux-date-range-picker
                                        range-start-label={this.rangeStartLabel}
                                        range-start-value={startDate}
                                        range-end-label={this.rangeEndLabel}
                                        range-end-value={endDate}
                                        date-format={this.dateFormat}
                                        min-date={this.minDate}
                                        max-date={this.maxDate}
                                        onUxDateRangeChange={
                                            this.handleDateRangeChange
                                        }
                                        todayText={this.translate("today")}
                                    />
                                </div>
                            </div>
                        )}
                        <div class="ux-row">
                            {filterData.filterBlocks.map((filterBlock) =>
                                renderFilterBlock(
                                    filterBlock,
                                    this.translate,
                                    this.filterColumnClass !== ""
                                        ? this.filterColumnClass
                                        : ""
                                )
                            )}
                        </div>
                        <div
                            class={{
                                ["ux-row"]: !this.disableTags,
                                ["ux-row--hidden"]: this.disableTags,
                            }}
                        >
                            <div class="ux-col-12 ux-u-mb-xs">
                                <div class="ux-tag-list" />
                                <span
                                    class="ux-filter__remove-all"
                                    data-testid="clear-all"
                                    role="button"
                                    onClick={this.handleRemoveAll}
                                >
                                    {t("filter.clearAll")}
                                    <ux-icon name="close" />
                                </span>
                            </div>
                        </div>
                    </ux-filter>
                )}
                {filterData && filterData.filterBlocks.length === 0 && (
                    <ux-filter />
                )}
                <div
                    class={`${this.baseClass}__spinner ${
                        pending ? `is-active` : ""
                    }`}
                >
                    <ux-loader variant="spinner"></ux-loader>
                </div>
                <slot name="translations" />
            </Host>
        );
    }
}
